import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import profileImage from '../../assets/profile.JPG';
import CybersecurityCapacityBuilding from '../../assets/CybersecurityCapacityBuilding.jpg';
import JanuaryFoodDriveProgram from '../../assets/JanuaryFoodDriveProgram.jpg';
import UjiFeedingInitiative from '../../assets/UjiFeedingInitiative.jpg';
import PanganiTreePlantingInitiative from '../../assets/PanganiTreePlantingInitiative.jpg';
import aacoseImage from '../../assets/news/aacose.jpg';
import codeafricaImage from '../../assets/news/codeafrica.jpg';
import kipraImage from '../../assets/news/kipra.jpg';
import treeGrowingImage from '../../assets/news/treegrowing.png';
import vitrayaImage from '../../assets/vitraya.jpg';
import scratchImage from '../../assets/scratchandscript.jpg';
import soapsImage from '../../assets/soaps.jpg';
import ieeeImage from '../../assets/printing.png';

const Portfolio = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [activeFilter, setActiveFilter] = useState('all');
  const [activeCategory, setActiveCategory] = useState('technical');

  const technicalProjects = [
    {
      id: 1,
      title: 'Security Operations Center (SOC) Build, Detection Engineering & Automation',
      category: 'technical',
      subcategory: 'security',
      image: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&w=500&q=60',
      description: 'Built and operationalized a Security Operations Center from the ground up, establishing detection pipelines, incident response workflows, and security monitoring across enterprise systems. Engineered SIEM visibility and custom detection logic to improve threat detection and reduce blind spots, while translating findings into actionable security hardening across infrastructure and identity systems. Designed and integrated AI-assisted workflows to enhance analyst efficiency, streamline triage, and strengthen response capabilities against evolving threats.',
      technologies: ['SOC Architecture', 'SIEM', 'SOAR', 'Detection Engineering', 'Incident Response', 'Security Automation', 'Threat Intelligence', 'Email Security', 'Network Security'],
      year: '2025 - Present'
    },
    {
      id: 2,
      title: 'Insurance, Blockchain & AI Platform',
      category: 'technical',
      subcategory: 'backend',
      image: vitrayaImage,
      description: 'Contributing to Vitraya\'s innovative platform that combines blockchain technology and AI to revolutionize insurance services and processes.',
      technologies: ['Blockchain', 'AI/ML', 'Smart Contracts', 'Cloud Architecture'],
      year: '2025',
      link: 'https://www.linkedin.com/posts/dr-vinita-saini-117943121_healthcareinnovation-insurtechafrica-healthinsurance-activity-7373265934683168768-Hokh?utm_source=share&utm_medium=member_desktop&rcm=ACoAACiDXOIBB6BShLmPKhN3xB7by_7IqBGhC-E'
    },
    {
      id: 3,
      title: 'The Impact of Generative AI on Online Safety',
      category: 'technical',
      subcategory: 'research',
      image: scratchImage, 
      description: 'Published research at iLabAfrica examining the implications of generative AI on online safety and content moderation.',
      technologies: ['AI Research', 'Online Safety', 'Content Moderation'],
      year: '2025',
      link: 'https://ilabafrica.strathmore.edu/the-impact-of-generative-ai-on-online-safety/'
    },
    {
      id: 4,
      title: 'AI-Integrated Backend System',
      category: 'technical',
      subcategory: 'backend',
      image: 'https://images.unsplash.com/photo-1555949963-aa79dcee981c?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=60',
      description: 'Developed scalable server-side architecture at Cybershield Industries that integrates AI models for data processing and real-time analytics.',
      technologies: ['Rust', 'Go', 'Node.js', 'TensorFlow', 'Docker'],
      year: '2024-2025'
    },
    {
      id: 5,
      title: 'Privacy-Preserving Mobile Money Protocols',
      category: 'technical',
      subcategory: 'research',
      image: soapsImage,
      description: 'Published research on enhanced security protocols for agent-facilitated mobile money services in Kenya, focusing on privacy preservation.',
      technologies: ['Cryptography', 'Mobile Money', 'Security Protocols'],
      year: '2025',
      link: 'https://www.researchgate.net/publication/387512363_Design_and_Evaluation_of_Privacy-Preserving_Protocols_for_Agent-Facilitated_Mobile_Money_Services_in_Kenya'
    },
    {
      id: 6,
      title: 'Blockchain KYC Solution',
      category: 'technical',
      subcategory: 'blockchain',
      image: 'https://images.unsplash.com/photo-1639762681057-408e52192e55?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=60',
      description: 'Designed and developed a self-sovereign identity KYC solution for the Power Learn Project LMS on the Cardano blockchain with secure API integration.',
      technologies: ['Cardano', 'Smart Contracts', 'REST API', 'Haskell'],
      year: '2022 - 2024'
    },
    {
      id: 7,
      title: 'Decentralized Healthcare Records System',
      category: 'technical',
      subcategory: 'research',
      image: ieeeImage,
      description: 'Research on blockchain-based solutions for secure management and interoperability of patient data across healthcare institutions.',
      technologies: ['Blockchain', 'Healthcare Systems', 'Data Interoperability'],
      year: '2024',
      link: 'https://www.researchgate.net/publication/384039093_Decentralized_Electronic_Health_Records_System_Secure_Management_and_Interoperability_of_Patient_Data?_tp=eyJjb250ZXh0Ijp7ImZpcnN0UGFnZSI6InByb2ZpbGUiLCJwYWdlIjoicHJvZmlsZSJ9fQ'
    },
    {
      id: 8,
      title: 'Cybersecurity Capacity Building',
      category: 'technical',
      subcategory: 'security',
      image: CybersecurityCapacityBuilding,
      description: 'Led comprehensive professional training programs in CEH, CHFI, and ICPT. Conducted industry-specific cybersecurity training for ABSA and DMI Kenya, and delivered ISACA training for the IGNITE program.',
      technologies: ['CEH', 'CHFI', 'ICPT', 'Digital Forensics'],
      year: '2022 - 2025'
    },
    {
      id: 9,
      title: 'Vulnerability Assessment & Penetration Testing',
      category: 'technical',
      subcategory: 'security',
      image: 'https://images.unsplash.com/photo-1510511459019-5dda7724fd87?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=60',
      description: 'Conducted comprehensive vulnerability assessments and penetration testing for mobile, web, and network applications at iLabAfrica-TAISOC, strengthening organizational security postures.',
      technologies: ['VAPT', 'Web Security', 'Network Security', 'Mobile Security'],
      year: '2022 - Present'
    },
    {
      id: 10,
      title: 'Cybersecurity Research & Publications',
      category: 'technical',
      subcategory: 'security',
      image: ieeeImage, 
      description: 'Published research papers and articles on cybersecurity topics, contributing to the advancement of security knowledge and best practices in the field.',
      technologies: ['Security Research', 'Technical Writing', 'Information Security'],
      year: '2022 - Present'
    },
    {
      id: 11,
      title: 'Malnutrition Management Application',
      category: 'technical',
      subcategory: 'research',
      image: 'https://images.unsplash.com/photo-1505751172876-fa1923c5c528?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=60',
      descriSption: 'Research and development of a mobile application for managing and tracking malnutrition cases.',
      technologies: ['Mobile Development', 'Healthcare', 'Data Analytics'],
      year: '2021',
      link: 'https://www.researchgate.net/publication/384039434_Malnutrition_management_mobile_application'
    }
  ];

  const communityProjects = [
    {
      id: 11,
      title: 'January Food Drive Program',
      category: 'community',
      subcategory: 'namalkia',
      image: JanuaryFoodDriveProgram,
      description: 'Collaborated with the Na Malkia team to implement our annual food drive initiative, working together to provide essential supplies to vulnerable communities in Nairobi.',
      impact: 'Supported 100 people with essential food supplies',
      year: '2025',
      link: 'https://www.instagram.com/p/DHuf9I_KLXO/'
    },
    {
      id: 12,
      title: 'Uji Feeding Initiative',
      category: 'community',
      subcategory: 'namalkia',
      image: UjiFeedingInitiative,
      description: 'Part of the Na Malkia team coordinating monthly feeding programs providing nutritious porridge to street families in Nairobi, establishing sustainable community support systems.',
      impact: 'Regular nutrition support for 100 individuals',
      year: '2025',
      link: 'https://www.instagram.com/p/DIdTkoks-Jt/'
    },
    {
      id: 13,
      title: 'Nguzo za Jamii Initiative',
      category: 'community',
      subcategory: 'namalkia',
      image: treeGrowingImage,  
      description: 'A school and community-based initiative aimed at revitalizing the collective role of families, schools, and local leaders in raising environmentally aware and socially responsible children. Establishing climate education clubs in three public primary schools in Pangani, Nairobi.',
      impact: 'Benefiting 1200+ learners, including 700+ girls',
      year: '2025',
      link: 'http://www.namalkia.co.ke'
    },
    {
      id: 14,
      title: 'Pangani Tree Planting Initiative',
      category: 'community',
      subcategory: ['environment', 'namalkia'],
      image: PanganiTreePlantingInitiative,
      description: 'Collaborated with the Na Malkia team on environmental conservation through tree planting activities at Pangani Primary School in Nairobi.',
      impact: 'Enhanced school environment and environmental awareness',
      year: '2025'
    },
    {
      id: 15,
      title: 'Nguzo za Jamii Environmental Program',
      category: 'community',
      subcategory: 'environment',
      image: treeGrowingImage,
      description: 'Creating safe and inclusive spaces where learners explore environmental topics, express creativity through art and upcycling, and participate in real-life sustainability activities. Organizing clean-up campaigns and environmental education initiatives.',
      impact: 'Promoting environmental awareness and sustainability',
      year: '2025',
      link: 'http://www.namalkia.co.ke'
    },
    {
      id: 16,
      title: 'Cybersecurity Training Program',
      category: 'community',
      subcategory: 'training',
      image: CybersecurityCapacityBuilding,
      description: 'Led comprehensive cybersecurity training programs, including ISACA training for the IGNITE program, covering digital forensics and cybersecurity essentials.',
      impact: 'Trained and mentored over 100 cybersecurity professionals',
      year: '2024'
    },
    {
      id: 17,
      title: 'Child Online Protection Initiative',
      category: 'community',
      subcategory: 'training',
      image: 'https://images.unsplash.com/photo-1484807352052-23338990c6c6?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=60',
      description: 'Developed and delivered training content on child online protection as part of Strathmore University\'s outreach campaigns.',
      impact: 'Reached over 500 children and parents with safety education',
      year: '2021 - 2022'
    }
  ];

  const allProjects = [...technicalProjects, ...communityProjects];
  
  const technicalFilters = [
    { id: 'all', label: 'All Tech' },
    { id: 'backend', label: 'Backend Development' },
    { id: 'blockchain', label: 'Blockchain' },
    { id: 'research', label: 'Research' },
    { id: 'security', label: 'Cyber Security' },
  ];
  
  const communityFilters = [
    { id: 'all', label: 'All Community' },
    { id: 'namalkia', label: 'Na Malkia Programs' },
    { id: 'environment', label: 'Environmental' },
    { id: 'training', label: 'Training & Mentorship' },
  ];

  // Get active filters based on selected category
  const activeFilters = activeCategory === 'technical' ? technicalFilters : communityFilters;
  
  // Get projects based on selected category
  const categoryProjects = activeCategory === 'technical' ? technicalProjects : communityProjects;
  
  // Filter projects based on selected filter within the category
  const filteredProjects = activeFilter === 'all' 
    ? categoryProjects 
    : categoryProjects.filter(item => 
        Array.isArray(item.subcategory) 
          ? item.subcategory.includes(activeFilter)
          : item.subcategory === activeFilter
      );

  return (
    <section ref={ref} className="py-20 bg-secondary-50 dark:bg-secondary-800">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-12 text-center"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 dark:text-white mb-4">
            My Portfolio
          </h2>
          <p className="text-secondary-600 dark:text-secondary-300 max-w-2xl mx-auto">
            Explore my work across technical innovation and community impact initiatives.
          </p>
          <div className="w-20 h-1 bg-primary-600 mx-auto mt-4"></div>
        </motion.div>

        {/* Profile Image */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-10 flex justify-center"
        >
          <div className="relative w-48 h-48 md:w-56 md:h-56 overflow-hidden rounded-full border-4 border-primary-600 shadow-xl">
            <img
              src={profileImage}
              alt="Profile"
              className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-primary-900 to-transparent opacity-0 hover:opacity-30 transition-all duration-300"></div>
          </div>
        </motion.div>

        {/* Category Toggle */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mb-8"
        >
          <div className="flex justify-center">
            <div className="inline-flex bg-white dark:bg-secondary-700 rounded-full p-1 shadow-md">
              <button
                onClick={() => {setActiveCategory('technical'); setActiveFilter('all');}}
                className={`px-6 py-2 rounded-full text-sm md:text-base transition-colors duration-300 ${
                  activeCategory === 'technical'
                    ? 'bg-primary-600 text-white'
                    : 'text-secondary-600 dark:text-secondary-300 hover:bg-secondary-100 dark:hover:bg-secondary-600'
                }`}
              >
                Technical Projects
              </button>
              <button
                onClick={() => {setActiveCategory('community'); setActiveFilter('all');}}
                className={`px-6 py-2 rounded-full text-sm md:text-base transition-colors duration-300 ${
                  activeCategory === 'community'
                    ? 'bg-primary-600 text-white'
                    : 'text-secondary-600 dark:text-secondary-300 hover:bg-secondary-100 dark:hover:bg-secondary-600'
                }`}
              >
                Community Impact
              </button>
            </div>
          </div>
        </motion.div>

        {/* Subcategory Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-12"
        >
          <div className="flex flex-wrap justify-center gap-2 md:gap-4">
            {activeFilters.map((filter) => (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                className={`px-4 py-2 rounded-full text-sm md:text-base transition-colors duration-300 ${
                  activeFilter === filter.id
                    ? 'bg-primary-600 text-white'
                    : 'bg-white dark:bg-secondary-700 text-secondary-600 dark:text-secondary-300 hover:bg-primary-100 dark:hover:bg-secondary-600'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </motion.div>


        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects.map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
              className="bg-white dark:bg-secondary-700 rounded-lg overflow-hidden shadow-custom group h-full flex flex-col"
            >
              <div className="relative overflow-hidden h-56">
                <img 
                  src={item.image} 
                  alt={item.title} 
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute top-4 right-4 bg-primary-600 text-white text-xs font-bold py-1 px-2 rounded">
                  {item.year}
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-secondary-900 to-transparent opacity-0 group-hover:opacity-70 transition-all duration-300 flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 transform translate-y-4 group-hover:translate-y-0">
                    {item.link ? (
                      <a
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-white text-primary-600 hover:bg-primary-50 px-6 py-2 rounded-full text-sm font-medium inline-block"
                      >
                        View Publication
                      </a>
                    ) : (
                      <button className="bg-white text-primary-600 hover:bg-primary-50 px-6 py-2 rounded-full text-sm font-medium">
                        View Details
                      </button>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="p-6 flex-grow flex flex-col">
                <div className="mb-4">
                  <h3 className="text-xl font-bold text-secondary-800 dark:text-white mb-3">
                    {item.title}
                  </h3>
                  <p className="text-secondary-600 dark:text-secondary-300 mb-4">
                    {item.description}
                  </p>
                </div>
                
                <div className="mt-auto">
                  {item.category === 'technical' && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {item.technologies.slice(0, 3).map((tech, i) => (
                        <span key={i} className="bg-secondary-100 dark:bg-secondary-600 text-secondary-800 dark:text-secondary-200 px-2 py-1 rounded text-xs">
                          {tech}
                        </span>
                      ))}
                      {item.technologies.length > 3 && (
                        <span className="bg-secondary-100 dark:bg-secondary-600 text-secondary-800 dark:text-secondary-200 px-2 py-1 rounded text-xs">
                          +{item.technologies.length - 3} more
                        </span>
                      )}
                    </div>
                  )}
                  
                  {item.category === 'community' && (
                    <div className="mt-2">
                      <span className="text-sm text-primary-600 dark:text-primary-400 font-medium">
                        Impact: {item.impact}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        
        {/* Empty State */}
        {filteredProjects.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
            className="text-center py-12"
          >
            <svg className="mx-auto h-16 w-16 text-secondary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="mt-4 text-lg font-medium text-secondary-800 dark:text-secondary-200">No projects found</h3>
            <p className="mt-2 text-secondary-600 dark:text-secondary-400">Try changing your filters to see more projects.</p>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default Portfolio;

